import React, { useMemo, useState } from 'react';
import { DraggableCore, DraggableData, DraggableEvent, DraggableEventHandler } from 'react-draggable';
import { animated, SpringValues, to } from '@react-spring/web';
import { DividerRenderElement, LayoutStage, RenderElementState } from './types';
import classNames from 'classnames';
import { throttle, uniqueId } from 'lodash';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import fastDeepEqual from 'fast-deep-equal';
import { createPropsEqualsChecker } from './utils/create-props-equals-checker';
import { useEditorStore } from './editor-store';
import { getRelativeCanvasPosition } from './utils/get-relative-canvas-position';

type UiStyle = 'default' | 'active' | 'invisible';

type Props = {
  divider: DividerRenderElement;
  style: SpringValues<RenderElementState> | RenderElementState;
  grabbing: boolean;
};

export const DividerView = ({ divider, style, grabbing }: Props) => {
  const contextMenuState = useEditorStore((state) => state.contextMenuState);
  const isDraggingNewBlock = useEditorStore((state) => state.isDraggingNewBlock);
  const appendingSection = useEditorStore((state) => state.appendingSection);
  const setContextMenuState = useEditorStore((state) => state.setContextMenuState);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);

  const contextMenuOpen =
    contextMenuState?.entity.type === 'section-divider' && contextMenuState?.entity.id === divider.id;
  let uiStyle: UiStyle = 'default';
  if (contextMenuOpen) {
    uiStyle = 'active';
  } else {
    if (divider.isLast) {
      if (isDraggingNewBlock && appendingSection) {
        uiStyle = 'active';
      } else {
        uiStyle = 'invisible';
      }
    } else {
      if (grabbing) {
        uiStyle = 'active';
      }
    }
  }

  return (
    <div className='absolute' onClick={() => clearSelectedBlock()}>
      {/* <DraggableCore onStart={onDividerStart} onDrag={onDividerDrag} onStop={onDividerStop}> */}
      <animated.div
        className={classNames(
          'group/divider !h-[40px] absolute flex-none rounded-2xl origin-center',
          'transition-[color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,filter,backdrop-filter]',
          {
            'z-neue-grid-block-elevated': grabbing,
            'pointer-events-none': divider.isLast,
          }
        )}
        style={style}
        onContextMenu={(e) => {
          e.preventDefault(); // prevent the default behaviour when right clicked
          e.stopPropagation();
          console.log('Right Click');
          const { pageX, pageY } = e;
          setContextMenuState({
            entity: {
              type: 'section-divider',
              id: divider.id,
              previousSectionId: divider.previousSectionId,
              nextSectionId: divider.nextSectionId,
            },
            ...getRelativeCanvasPosition(pageX, pageY, 96),
          });
        }}
      >
        {/* <DraggableCore onStart={onDividerStart} onDrag={onDividerDrag} onStop={onDividerStop}> */}
        <div
          className={classNames(
            'absolute h-[40px] top-0 left-0 -translate-y-1/2 w-full bg-neue-canvas-bg rounded-lg transition',
            {
              'cursor-grab bg-neue-canvas-bg/40': grabbing,
              'cursor-default': !grabbing,
            }
          )}
        ></div>
        {/* </DraggableCore> */}
        <div
          className={classNames('pointer-events-none absolute top-0 h-full transition-all', {
            'left-10 right-10': grabbing,
            'left-0 right-0': !grabbing,
            'text-neue-canvas-fg': uiStyle === 'active',
            'text-neue-canvas-fg-10 group-hover/divider:text-neue-canvas-fg': uiStyle === 'default',
            'text-neue-canvas-fg opacity-0': uiStyle === 'invisible',
          })}
        >
          <div
            className={classNames(
              'w-full h-full flex items-center justify-center gap-4 group-hover/divider:opacity-100 transition-opacity',
              {
                'opacity-100': uiStyle === 'active',
                'opacity-20': uiStyle !== 'active',
              }
            )}
          >
            {divider.nextSectionIndex !== null && (
              <div className='text-neue-journey-medium-strong text-neue-journey-bg'>{divider.nextSectionIndex + 1}</div>
            )}
            <div className='grow h-[3px] scale-y-50 rounded-full bg-neue-canvas-fg group-hover/divider:bg-neue-canvas-fg' />
          </div>
        </div>
      </animated.div>
      {/* </DraggableCore> */}
    </div>
  );
};

export const MemoizedDividerView = React.memo(DividerView, createPropsEqualsChecker([], 'DividerView'));
